import type { PartnerCockpitOfferRequestContact } from '@houzy/schemas/partner-cockpit/offer-requests';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import Link from '@mui/material/Link';
import Stack from '@mui/material/Stack';
import type { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';

export type ContactViaPhoneEmailDialogProps = {
    open: boolean;
    // 'confirm' asks before notifying the customer, 'revealed' shows their contact details afterwards.
    step: 'confirm' | 'revealed';
    contact: PartnerCockpitOfferRequestContact;
    confirmDisabled?: boolean;
    onConfirm: () => void;
    onCancel: () => void;
    onDone: () => void;
};

export function ContactViaPhoneEmailDialog({
    open,
    step,
    contact,
    confirmDisabled = false,
    onConfirm,
    onCancel,
    onDone,
}: ContactViaPhoneEmailDialogProps): ReactElement {
    const { t } = useTranslation();

    return (
        <Dialog
            open={open}
            onClose={step === 'confirm' ? onCancel : onDone}
            maxWidth="sm"
            fullWidth
            aria-labelledby="contact-phone-email-dialog-title">
            <DialogTitle id="contact-phone-email-dialog-title">
                {t('OFFER_REQUESTS.CONTACT.PHONE_EMAIL_DIALOG.TITLE')}
            </DialogTitle>
            {step === 'confirm' ? (
                <>
                    <DialogContent>
                        <DialogContentText>{t('OFFER_REQUESTS.CONTACT.PHONE_EMAIL_DIALOG.BODY')}</DialogContentText>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={onCancel}>{t('OFFER_REQUESTS.CONTACT.PHONE_EMAIL_DIALOG.CANCEL')}</Button>
                        <Button variant="contained" disabled={confirmDisabled} onClick={onConfirm}>
                            {t('OFFER_REQUESTS.CONTACT.PHONE_EMAIL_DIALOG.CONFIRM')}
                        </Button>
                    </DialogActions>
                </>
            ) : (
                <>
                    <DialogContent>
                        <DialogContentText>
                            {t('OFFER_REQUESTS.CONTACT.PHONE_EMAIL_DIALOG.REVEALED_BODY', { name: contact.name })}
                        </DialogContentText>
                        <Stack>
                            {contact.phone !== undefined && (
                                <Link href={`tel:${contact.phone}`} sx={{ fontWeight: 'bold' }}>
                                    {contact.phone}
                                </Link>
                            )}
                            {contact.email !== undefined && (
                                <Link href={`mailto:${contact.email}`} sx={{ fontWeight: 'bold' }}>
                                    {contact.email}
                                </Link>
                            )}
                        </Stack>
                    </DialogContent>
                    <DialogActions>
                        <Button variant="contained" onClick={onDone}>
                            {t('OFFER_REQUESTS.CONTACT.PHONE_EMAIL_DIALOG.DONE')}
                        </Button>
                    </DialogActions>
                </>
            )}
        </Dialog>
    );
}
